import { arrayOf, bool, func } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Col, Input, Label, Row } from 'reactstrap';

import CategoryContext from '../../generic/CategorySelect/CategoryContext';
import ScopedDrawer, {
  approval,
  changelog,
  comments,
} from '../../generic/ScopedDrawer';
import SubmittableForm from '../../generic/SubmittableForm';
import { getTooltipButtonWithText } from '../../generic/Tooltip';
import Titled from '../../layout/Titled';
import { isSuperuser } from '../../layout/Variant/Variant';
import withProducts from '../../products/withProducts/with-products';
import { productShape, quoteShape } from '../../prop-types';
import API from '../../REST/api';
import FEATURE_FLAGS, {
  useSafeFlag,
  useSafeUnleashContext,
} from '../../utils/flags';
import OnsiteAverageDailyRate from '../OnsiteAverageDailyRate.container';
import ProductSelection from '../ProductSelection.container';
import QuoteDownloadOptions from '../QuoteDownloadOptions';
import QuoteForm from '../QuoteForm.container';
import ConnectedQuoteLineFilters from '../QuoteLineFilters';
import QuoteLineTable from '../QuoteLineTable.container';
import HELP_TEXTS from '../utils/help-texts';
import ApplyCWTemplateButton from './ApplyCWTemplateButton.container';
import DeleteQuoteButton from './DeleteQuoteButton';
import QuoteLineBundleSelection from './QuoteLineBundleSelection.container';
import QuoteLinesBulkEditor from './QuoteLinesBulkEditor.container';
import QuoteLineSelectionToggle from './QuoteLineSelectionToggle.container';

const ButtonWithTooltip = getTooltipButtonWithText(HELP_TEXTS);
function QuoteEditor({
  model,
  onSubmit,
  isQuoteDirty,
  products,
  toggleCommercialIncentiveModal,
  isRequestInProgress,
}) {
  const [showDetails, setShowDetails] = useState(false);
  const { salesforceId, opportunityId } = model;
  const isSalesforceQuote = !!salesforceId;
  const setUnleashContext = useSafeUnleashContext();
  const isDraftCampaignEnabled = useSafeFlag(FEATURE_FLAGS.DRAFT_CAMPAIGN);

  // Update Unleash context with account SF ID from opportunity
  useEffect(() => {
    if (!setUnleashContext || !opportunityId) return;

    const fetchAndUpdateContext = async () => {
      try {
        const response = await API.getOpportunity({ id: opportunityId });
        const opportunity = response?.value || response;
        const accountSfId = opportunity?.accountSfId;

        if (accountSfId) {
          setUnleashContext({
            userId: String(accountSfId),
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    };

    fetchAndUpdateContext();
  }, [opportunityId, setUnleashContext]);

  return (
    <SubmittableForm
      onSubmit={onSubmit}
      loading={isRequestInProgress}
      className="c-quote-editor wide-container-fluid"
    >
      <div className="mb-4">
        <Titled
          title={`${model.number} - ${model.campaignObjective}`}
          subHeader={
            isSalesforceQuote ? `Salesforce ID - ${model?.salesforceId}` : ''
          }
          sticky
          actions={[
            !isSalesforceQuote && <DeleteQuoteButton model={model} />,
            <QuoteDownloadOptions
              tag={ButtonWithTooltip}
              additionalButtonProps={{
                helpKey: 'quoteEditor',
                section: 'download',
                showTooltip: isQuoteDirty,
              }}
              disabled={isQuoteDirty}
              quote={model}
            />,
            onSubmit && (
              <Button
                type="submit"
                color="primary"
                disabled={isRequestInProgress}
                className="ml-2"
              >
                Save
              </Button>
            ),
          ]}
        >
          <ScopedDrawer
            options={[
              comments({
                contentType: 'opportunity',
                objectId: model.opportunityId,
              }),
              changelog({
                contentType: 'quote',
                objectId: model.id,
              }),
              approval({
                contentType: 'opportunity',
                objectId: model.opportunityId,
                quoteId: model.id,
              }),
            ]}
          >
            <QuoteForm
              toggleCommercialIncentiveModal={toggleCommercialIncentiveModal}
            />
          </ScopedDrawer>
        </Titled>
      </div>

      <Row className="justify-content-between">
        <Col sm={6} className="d-flex">
          <div className="flex-grow-1 mr-2">
            {isSuperuser() && <ProductSelection />}
          </div>
          <div className="flex-grow-1">
            {isSuperuser() && <QuoteLineBundleSelection />}
          </div>
        </Col>
        <Col sm="auto">
          <OnsiteAverageDailyRate
            boxView
            helpText={HELP_TEXTS.onsiteAverage.dailyRate}
          />
        </Col>
      </Row>

      <div className="py-2 bg-white c-campaign-sticky-level-2">
        <Row className="mb-2">
          <Col sm={12}>
            <CategoryContext.Consumer>
              {categories => (
                <ConnectedQuoteLineFilters
                  allCategories={categories}
                  allProducts={products}
                />
              )}
            </CategoryContext.Consumer>
          </Col>
        </Row>
        <Row className="my-3 justify-content-between">
          <Col sm="auto">
            <QuoteLineSelectionToggle />
          </Col>
          <Label sm="auto" className="d-flex">
            <Input
              type="checkbox"
              checked={showDetails}
              onChange={event => setShowDetails(event.target.checked)}
            />
            Show product configuration inline
          </Label>
          <Col sm="auto">
            <ApplyCWTemplateButton />
            <QuoteLinesBulkEditor />
          </Col>
        </Row>
      </div>

      <QuoteLineTable
        showDetails={showDetails}
        isDraftCampaignEnabled={isDraftCampaignEnabled}
      />
    </SubmittableForm>
  );
}

QuoteEditor.propTypes = {
  model: quoteShape.isRequired,
  onSubmit: func.isRequired,
  toggleCommercialIncentiveModal: func.isRequired,
  isRequestInProgress: bool.isRequired,
  isQuoteDirty: bool.isRequired,
  products: arrayOf(productShape.isRequired).isRequired,
};

export default withProducts({})(QuoteEditor);
